import * as React from "react";
import { useState } from "react";
import { Stack, Text, IconButton, TooltipHost, mergeStyles, getTheme } from "@fluentui/react";
import { BankInfo } from "./types";

const theme = getTheme();

const cardStyle = mergeStyles({
  border: `1px solid ${theme.palette.neutralLight}`,
  borderRadius: "4px",
  padding: "8px 12px",
  backgroundColor: theme.palette.neutralLighterAlt,
});

const fieldRowStyle = mergeStyles({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "4px 0",
  borderBottom: `1px solid ${theme.palette.neutralLight}`,
  selectors: {
    ":last-child": { borderBottom: "none" },
  },
});

export const FieldRow: React.FunctionComponent<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className={fieldRowStyle}>
    <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
      {label}
    </Text>
    {children}
  </div>
);

export interface BankInfoCardProps {
  bank: BankInfo;
  children?: React.ReactNode; // extra rows (e.g. metrics on the Info page)
}

const BankInfoCard: React.FunctionComponent<BankInfoCardProps> = ({ bank, children }) => {
  const [copied, setCopied] = useState(false);

  const handleCopyCert = () => {
    navigator.clipboard.writeText(String(bank.cert)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className={cardStyle}>
      <Stack tokens={{ childrenGap: 0 }}>
        <FieldRow label="Name">
          <Text styles={{ root: { fontWeight: 600 } }}>{bank.name}</Text>
        </FieldRow>
        <FieldRow label="Location">
          <Text>{[bank.city, bank.state].filter(Boolean).join(", ") || "—"}</Text>
        </FieldRow>
        <FieldRow label="Cert #">
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
            <Text>{bank.cert}</Text>
            <TooltipHost content={copied ? "Copied!" : "Copy cert number"}>
              <IconButton
                iconProps={{ iconName: copied ? "CheckMark" : "Copy" }}
                onClick={handleCopyCert}
                styles={{
                  root: { height: 24, width: 24 },
                  icon: { fontSize: 12, color: copied ? theme.palette.green : theme.palette.themePrimary },
                }}
              />
            </TooltipHost>
          </Stack>
        </FieldRow>
        {children}
      </Stack>
    </div>
  );
};

export default BankInfoCard;
